import { useState, useCallback, useRef } from 'react';
import { Search, CheckCircle2, AlertCircle, Loader2, X, MapPin } from 'lucide-react';
import { searchByName } from '../services/nerisBoundaryService';
import { buildSelectedBoundary } from '../utils/boundaryUtils';
import type { BoundaryGeometry } from '../utils/boundaryUtils';

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA',
  'KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
  'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT',
  'VA','WA','WV','WI','WY','DC',
];

interface SearchResult {
  nerisId: string;
  departmentName?: string;
  departmentType?: string;
  state?: string;
  stateCode?: string;
  boundary: BoundaryGeometry | null;
}

export interface BoundarySearchPanelProps {
  tenantName?: string;
  currentNerisId?: string;
  hasBoundary: boolean;
  onSelect: (boundary: BoundaryGeometry, nerisId: string, centroid: [number, number], bbox: [number, number, number, number], area: number) => void;
  onClear: () => void;
  theme: any;
}

export default function BoundarySearchPanel({
  tenantName,
  currentNerisId,
  hasBoundary,
  onSelect,
  onClear,
  theme,
}: BoundarySearchPanelProps) {
  const [query, setQuery] = useState(tenantName ?? '');
  const [stateFilter, setStateFilter] = useState('');
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState<SearchResult[]>([]);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleSearch = useCallback(async () => {
    const q = query.trim();
    if (!q) return;
    setSearching(true);
    setSearchError(null);
    setHasSearched(true);
    try {
      const res = await searchByName(q, stateFilter || undefined);
      setResults(res as SearchResult[]);
    } catch (err: any) {
      setSearchError(err?.message || 'Search failed — check connection');
      setResults([]);
    } finally {
      setSearching(false);
    }
  }, [query, stateFilter]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch();
  };

  const handleSelect = (result: SearchResult) => {
    const boundary = buildSelectedBoundary(
      result.nerisId,
      result.departmentName ?? '',
      result.departmentType,
      result.boundary,
    );
    if (!boundary) return;
    onSelect(boundary.geometry, boundary.nerisId, boundary.centroid, boundary.bbox, boundary.area);
    setResults([]);
    setHasSearched(false);
  };

  return (
    <div className="space-y-3">
      {/* Current boundary status */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {hasBoundary ? (
            <>
              <CheckCircle2 className="h-4 w-4 text-green-500 shrink-0" />
              <span className="text-sm font-medium" style={{ color: theme.textPrimary }}>
                Boundary set
                {currentNerisId && (
                  <span className="ml-1.5 font-mono text-xs px-1.5 py-0.5 rounded bg-gray-100" style={{ color: theme.textSecondary }}>
                    {currentNerisId}
                  </span>
                )}
              </span>
            </>
          ) : (
            <>
              <AlertCircle className="h-4 w-4 text-amber-500 shrink-0" />
              <span className="text-sm" style={{ color: theme.textSecondary }}>No boundary set</span>
            </>
          )}
        </div>

        {hasBoundary && (
          showClearConfirm ? (
            <div className="flex items-center gap-1.5 text-xs">
              <span style={{ color: theme.textSecondary }}>Clear boundary?</span>
              <button
                type="button"
                onClick={() => { onClear(); setShowClearConfirm(false); }}
                className="px-2 py-0.5 rounded text-red-600 bg-red-50 hover:bg-red-100 font-medium"
              >
                Yes
              </button>
              <button
                type="button"
                onClick={() => setShowClearConfirm(false)}
                className="px-2 py-0.5 rounded hover:bg-gray-100 font-medium"
                style={{ color: theme.textSecondary }}
              >
                Cancel
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setShowClearConfirm(true)}
              className="flex items-center gap-1 text-xs hover:text-red-500 transition-colors"
              style={{ color: theme.textSecondary }}
            >
              <X className="h-3 w-3" />
              Clear
            </button>
          )
        )}
      </div>

      {/* Search bar */}
      <div className="flex gap-2">
        <div className="flex-1 flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-sm" style={{ background: theme.cardBg, borderColor: theme.cardBorder }}>
          <Search className="h-3.5 w-3.5 shrink-0" style={{ color: theme.textMuted }} />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Search fire departments by name…"
            className="flex-1 bg-transparent outline-none text-sm placeholder-gray-400"
            style={{ color: theme.textPrimary }}
          />
        </div>
        <select
          value={stateFilter}
          onChange={(e) => setStateFilter(e.target.value)}
          className="px-2 py-1.5 rounded-lg border text-xs outline-none"
          style={{ background: theme.cardBg, borderColor: theme.cardBorder, color: theme.textPrimary }}
        >
          <option value="">All states</option>
          {US_STATES.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
        <button
          type="button"
          onClick={handleSearch}
          disabled={searching || !query.trim()}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-white transition-colors disabled:opacity-50"
          style={{ background: theme.accent }}
        >
          {searching ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Search className="h-3.5 w-3.5" />}
          Search
        </button>
      </div>

      {/* Error */}
      {searchError && (
        <p className="text-xs text-red-500 flex items-center gap-1">
          <AlertCircle className="h-3.5 w-3.5 shrink-0" />
          {searchError}
        </p>
      )}

      {/* Results */}
      {results.length > 0 && (
        <div className="rounded-lg border overflow-hidden" style={{ borderColor: theme.cardBorder }}>
          {results.map((r) => (
            <button
              key={r.nerisId}
              type="button"
              onClick={() => handleSelect(r)}
              className="w-full flex items-start gap-2.5 px-3 py-2.5 text-left hover:bg-orange-50 transition-colors border-b last:border-b-0"
              style={{ borderBottomColor: theme.cardBorder, background: theme.cardBg }}
            >
              <MapPin className="h-4 w-4 mt-0.5 shrink-0" style={{ color: r.boundary ? theme.accent : theme.textMuted }} />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate" style={{ color: theme.textPrimary }}>
                  {r.departmentName ?? r.nerisId}
                </div>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className="text-xs font-mono" style={{ color: theme.textSecondary }}>{r.nerisId}</span>
                  {r.state && <span className="text-xs" style={{ color: theme.textSecondary }}>· {r.state}</span>}
                  {r.departmentType && (
                    <span className="text-xs capitalize" style={{ color: theme.textMuted }}>· {r.departmentType.toLowerCase()}</span>
                  )}
                </div>
              </div>
              {!r.boundary && (
                <span className="text-xs shrink-0 mt-0.5" style={{ color: theme.textMuted }}>No geometry</span>
              )}
              {r.boundary && (
                <span className="text-xs shrink-0 mt-0.5 text-green-600">Has boundary</span>
              )}
            </button>
          ))}
        </div>
      )}

      {/* No results */}
      {hasSearched && !searching && results.length === 0 && !searchError && (
        <p className="text-xs text-center py-2" style={{ color: theme.textSecondary }}>
          No departments found. Try a different name or state filter.
        </p>
      )}
    </div>
  );
}
